// core-preference-integration.service.ts
import { BaseApiService } from "../base/base-api.service";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CustomErrorHandlerService } from "../custom-error-handler.service";
import { CorePreferenceIntegration } from "../../../models/core-preference-integration/core-preference-integration.model";
import { CorePreferenceIntegrationSerializer } from "../../../models/core-preference-integration/core-preference-integration-serializer.class";
import { CorePreference } from "../../../models/core-preferences/core-preferences.model";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * Service to manage Core Preference Integrations with Compliance Inputs
 */
@Injectable()
export class CorePreferenceIntegrationService extends BaseApiService<CorePreferenceIntegration> {
  constructor(httpClient: HttpClient) {
    super(
      httpClient,
      null,
      "corepreference-integrations",
      new CorePreferenceIntegrationSerializer(),
      new CustomErrorHandlerService()
    );
  }

  /**
   * Get an integration by preference ID
   * @param preferenceId The ID of the core preference
   */
  /*getByPreferenceId(preferenceId: string): Observable<CorePreferenceIntegration> {
    return this.http.get<any>(`<span class="math-inline">\{this\.url\}/preference/</span>{preferenceId}`, this.httpOptions) // 'http' should be 'this.httpClient' if BaseApiService exposes it directly, or 'this.http' if it's an alias within BaseApiService
      .pipe(
        map((data: any) => this.serializer.fromJson(data) as CorePreferenceIntegration) // Explicit cast if you are sure serializer returns the correct type
      );
  }*/

  /**
   * Get only active integrations
   */
  getActiveIntegrations(): Observable<CorePreferenceIntegration[]> {
    return this.httpClient
      .get(`${this.url}/corepreference-integrations/active`)
      .pipe(map((data: any) => data.map((item: any) => this.serializer.fromJson(item))));
  }

  /**
   * Get available core preferences that don't have integrations yet
   */
  getAvailablePreferences(): Observable<CorePreference[]> {
    return this.httpClient
      .get(`${this.url}/corepreference-integrations/available-preferences`)
      .pipe(map((data: any) => data));
  }

  /**
   * Check if a preference already has an integration
   * @param preferenceId The ID of the core preference
   */
  hasIntegration(preferenceId: string): Observable<boolean> {
    return this.httpClient
      .get(`${this.url}/corepreference-integrations/has-integration/${preferenceId}`)
      .pipe(map((data: any) => data));
  }

  /**
   * Test an integration with sample data
   * @param id Integration ID to test
   * @param request Test data containing email, value, country and state
   */
  testIntegration(id: string, request: any): Observable<any> {
    return this.httpClient
      .post(`${this.url}/corepreference-integrations/test/${id}`, request)
      .pipe(map((data: any) => data));
  }

  /**
   * Helper method to enrich integration objects with display names
   * @param integrations List of integrations
   * @param preferences List of core preferences
   * @param translateService Translation service for localized text
   */
  enrichIntegrations(
    integrations: CorePreferenceIntegration[],
    preferences: CorePreference[],
    translateService: any
  ): CorePreferenceIntegration[] {
    return integrations.map(integration => {
      const corePreference = preferences.find(cp => cp.id === integration.corePreferenceId);
      
      const statusDisplay = integration.status === 'active' 
        ? translateService.instant('PREFERENCES.ENABLED') 
        : translateService.instant('PREFERENCES.DISABLED');
        
      const typeDisplay = integration.integrationType === 'WholePreference'
        ? translateService.instant('PREFERENCES.WHOLE_PREFERENCE')
        : translateService.instant('PREFERENCES.BY_CHANNEL');
      
      return {
        ...integration,
        preferenceDisplay: corePreference ? corePreference.name : 'Unknown',
        statusDisplay: statusDisplay,
        integrationTypeDisplay: typeDisplay
      };
    });
  }
}